import React, { useState, useEffect, useRef } from "react";
import Home from "./Home";
import Navbar from "../components/Navbar";
import Register from "./login/Register";
import Login from "./login/Login";
import { Route, Switch, Redirect, useHistory, useLocation } from "react-router-dom";
import { Container } from "react-bootstrap";
import ForgetPassword from "./login/ForgetPassword";
import ChangePassword from "./login/ChangePassword";
import Protected from "./login/Protected";
import AdminConsole from "./admin/AdminConsole";
// Superseded by Manage workspace (workspaces/ManageWorkspace.jsx):
// import Workspace from "./workspace/Workspace";
import ManageWorkspace from "./workspaces/ManageWorkspace";
import DevelopersPage from "./developers/DevelopersPage";
import ErrorBoundary from "./app/ErrorBoundary";
import { canOpenConsole, hasNoDatabase, NO_DATABASE_MSG } from "../utils/permissions";
import { DATABASE_FREE_PATHS, canonicalLink, databaseInLink } from "../utils/deepLinks";
import { isDatabaseUnreachable, unreachableMessage } from "../utils/databaseOffline";
import NoDatabaseCard from "./app/NoDatabaseCard";
import ContextNote from "./app/ContextNote";
import { applyContext, currentWorkspace, rememberWorkspace, workspacesOf } from "../utils/readingContext";
import Footer from "./Footer";
import ConfirmationMsg from "./login/ConfirmationMsg";
import FailureMsg from "./login/FailureMsg";
import VisualizeSolMan from "./SAP-SolMan/Visualize-SolMan";
import SPDocService from "./login/SPDocService";
import MyAccount from "./login/MyAccount";
import ResetPasswordMsg from "./login/ResetPasswordMsg";
// Demo org chart ("ROOT", "E2E-Map", …) from the first prototype: it showed one
// customer's process names to everyone. /relationship-explorer/:id now opens the
// real explorer (CustomD3Tree). Kept for reference, routed nowhere.
// import RelationshipExplorer from "./relationshipExplorer";
import Chat from "./bfChatBot/Chat";
import FlowOS from "./flowosChat/FlowOS";
import CustomD3Tree from "./customD3Tree";
// Static-hierarchy viewer, replaced by the dynamic DiagramViewer. Kept for reference.
// import ModelRenderer from "./modelRenderer";
import DiagramViewer from "./diagram/DiagramViewer";
import InvitePage from "./invite/InvitePage";
import SetupPage from "./setup/SetupPage";
import RelaseNotes from "./ReleaseNotes";
import axios from "axios";
import SessionExpiredModal from "./login/SessionExpiredModal";
import { ViewConfigProvider } from "./app/ViewConfigContext";
import { SHOW_DEVELOPER_API } from "../features";
import readFeatures from "../utils/featureFlags";


import {
  getProfile,
  getExplorerStart,
  connectDb,
  connectInitialDb,
  recordSignOut,
} from "../actions";
import SplashScreen from "../screens/SplashScreen";
import NotFoundPage from "./notFoundPage/404";
import AdvanceSearch from "./advanceSearch/AdvanceSearch";
import ExternalLinkMessage from "./externalLinkMSG/ExternalLInkMessage";
//pipe-line test
var CryptoJS = require("crypto-js");

const App = (props) => {
  const history = useHistory();
  const location = useLocation();

  // Pages that fill the viewport (no page scroll). Everything else scrolls by default.
  const noScrollPaths = ["/home", "/relationship-explorer", "/object", "/map/", "/model/", "/flowos", "/advance-search", "/admin"];
  const isNoScroll = noScrollPaths.some((p) => location.pathname.includes(p));
  // Signed-out account pages render full screen with their own header and
  // footer. `/auth/reset-password/:token` is one of them since 29 Sep 2026:
  // it moved onto AuthLayout with the rest, and without it here the layout
  // would draw its own header and footer inside the app's.
  const accountPagePattern = /^\/($|login(\/|$)|signup(\/|$)|invite\/|setup\/?$|forget-password\/?$|changepassword\/?$|auth\/reset-password\/)/;
  // The pages the ordinary sign-in flow lands on before the first-login
  // reload below — kept going to the parameterised home route, exactly as
  // before. Anywhere else (e.g. an invitation link reached via Login's
  // ?next=) is where the person actually wants to end up, so the reload
  // returns there instead of clobbering it with home.
  const defaultLandingPagePattern = /^\/(home\/?$|$|login(\/|$)|signup(\/|$)|forget-password\/?$|changepassword\/?$)/;

  const [sessionExpired, setSessionExpired] = useState(false);
  // Signed in, but no database to open (see NoDatabaseCard). The profile and
  // the axios interceptor ask the same question (utils/permissions
  // hasNoDatabase), so the card never shows for someone who has somewhere to
  // go - and a later profile that lists a database clears it.
  const [noDatabase, setNoDatabase] = useState(false);
  // The profile as loaded, for the interceptor's copy of that question.
  const loadedUser = useRef(null);
  // Set when the profile could not be read because of the server, not the account.
  const [startupError, setStartupError] = useState(null);
  // Set when this user's database itself can't be reached: the server's own
  // words for it (offline, unavailable, credentials), and which database they
  // are about. Home, the explorer, search and the database chip show it, so
  // nobody is left to guess at an empty screen. It never signs anyone out -
  // the account, the console and the database switcher all still work.
  const [databaseOutage, setDatabaseOutage] = useState(null);

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const isPublicEndpoint = error?.config?.url?.includes("/neoPublic/");
        // A refused action ("forbidden") is not an expired session.
        const isRefusedAction = error?.response?.data?.reason === "forbidden";
        // Access to every database ended while signed in: say whom to ask.
        if (error?.response?.status === 400 && error?.response?.data?.msg === NO_DATABASE_MSG && !isPublicEndpoint) {
          if (hasNoDatabase(loadedUser.current, { serverSaysSo: true })) setNoDatabase(true);
        }
        if (error?.response?.status === 403 && !isPublicEndpoint && !isRefusedAction) {
          localStorage.clear();
          history.push("/login");
          setSessionExpired(true);
        }
        // A server error carries a reference; make sure it reaches whatever
        // message the screen shows, so a user can quote it to support.
        if (error?.response?.status >= 500) {
          const data = error.response.data && typeof error.response.data === "object" ? error.response.data : {};
          const reference = data.reference || error.response.headers?.["x-request-id"];
          if (reference && !String(data.msg || "").includes(reference)) {
            error.response.data = {
              ...data,
              msg: `${data.msg || "Something went wrong on our side."} (reference ${reference})`,
              reference,
            };
          }
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, [history]);

  const handleSessionLogin = () => {
    setSessionExpired(false);
  };

  const [nodeDatum, setNodeDatum] = useState(null);

  const authToken = localStorage.getItem("auth");
  const isAccountPage = !authToken && accountPagePattern.test(location.pathname);

  const [loading, setLoading] = useState(true);
  // Every screen reads the database itself. The four whole-hierarchy trees this
  // used to load at sign-in (and cache, encrypted, in localStorage) described
  // one customer's model and are gone with the screens that read them; the
  // static hierarchy viewer, if it ever returns, fetches its own.
  const [rootGUID, setRootGUID] = useState(null);

  const [user, setUser] = useState({
    id: "",
    firstName: "",
    lastName: "",
    email: "",
    subscription: "",
    expirationDate: "",
    role: "",
    expired: "",
    databases: [],
    organisations: [],
    mainDatabase: "",
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const mainDb = localStorage.getItem("db");
        if (mainDb === "neo4j") {
          await connectDb();
        } else {
          await connectInitialDb();
        }

        // ReactGA.initialize("G-XCK3MC5WGS");
        // ReactGA.pageview(window.location.pathname + window.location.search);

        if ((authToken && authToken.length > 1) || authToken !== null) {
          let res = await getProfile(authToken);

          if (res.success) {
            const userRes = res.content;
            // Store feature flags from server — refreshed on every page load
            if (userRes.features) {
              localStorage.setItem("features", JSON.stringify(userRes.features));
            }
            setUser(userRes);
            loadedUser.current = userRes;

            // Signed in, but no database to open: a clear card, not pages
            // that fail. A profile with one clears the card again.
            const nowhereToGo = hasNoDatabase(userRes);
            setNoDatabase(nowhereToGo);
            if (nowhereToGo) {
              setLoading(false);
              return;
            }

            // What this person is looking at, settled once and in one order:
            // the workspace first, then a database that workspace gives them,
            // and the view follows the database through ViewConfigProvider
            // below (utils/readingContext.js says what decides each). A link
            // that names a database asks for that one, and can move the
            // workspace with it. Memberships change, so every remembered
            // choice is checked against the profile that has just arrived.
            const remembered = localStorage.getItem("db");
            const context = applyContext(userRes, { asked: databaseInLink(location.pathname) });
            // The workspace chosen gives this person no database at all:
            // say so, rather than leaving another workspace's library open.
            if (context.empty) {
              setNoDatabase(true);
              setLoading(false);
              return;
            }
            let mainDb = remembered ? context.database : null;
            // A link into a database they can't open: the address names an
            // object they never had, so the way back is that database's home
            // rather than a page that will only say "not found".
            if (context.refused) history.replace(`/home/${context.database}`);
            // The database in use changed while settling all this - a link
            // into another one, or one that went away. The session was opened
            // on the old one a moment ago, so it is opened again.
            if (context.moved) await connectInitialDb();
            if (mainDb) {
              setUser({ ...userRes, mainDatabase: mainDb });
            } else {
              setUser({ ...userRes });
              // applyContext has already written it; the first login is only
              // the case where nothing was remembered to check it against.
              localStorage.setItem("userId", userRes.id);
              // On a default landing page (root, login, signup, …) go to the
              // usual parameterised home route. Otherwise the browser is
              // already sitting on the page sign-in was asked to return to
              // (e.g. /invite/:token via Login's ?next=) — stay there.
              const firstLoginTarget = defaultLandingPagePattern.test(location.pathname)
                ? `/home/${context.database}`
                : `${location.pathname}${location.search}`;
              history.push(firstLoginTarget);
              history.go(0);
              return; // first login — page is reloading, stop here
            }

            // Where this database's explorer starts (an admin's choice in
            // Views, else its root): the Home tile and the menu item link to
            // it. The explorer asks again when "Start over" is pressed, so a
            // later change needs no reload.
            const start = await getExplorerStart();
            setRootGUID(start.guid);
            // No starting point is one thing; a database that answered
            // nothing at all is another, and only the second is an outage.
            setDatabaseOutage(
              isDatabaseUnreachable(start)
                ? { database: mainDb || userRes.mainDatabase, reason: start.reason, msg: unreachableMessage(start) }
                : null
            );

            var subscriptionCipher = CryptoJS.AES.encrypt(
              userRes?.subscription,
              process.env.REACT_APP_STORAGE_KEY || "my-secret-key@123"
            ).toString();

            localStorage.setItem("subscription", subscriptionCipher);
            localStorage.setItem("userId", userRes.id);

            setLoading(false);
          } else if (res.network || res.status >= 500) {
            // The server or one of its databases is away (503 offline /
            // unavailable / credentials): say so and keep the session, so
            // a blip never signs anyone out.
            setStartupError(
              res.network
                ? "Business Flows can't be reached right now. Check your connection and try again."
                : res.msg || "Something went wrong on our side. Try again shortly."
            );
          } else {
            // Profile fetch failed — clear stale data and return to login.
            // Do NOT show the session-expired modal here; that modal is reserved
            // for confirmed JWT expiry (403 from authenticated routes via the interceptor).
            localStorage.clear();
            history.push("/login");
          }
        } else {
          // history.push("/login");
        }
      } catch (err) {
        console.error("Error occured while initializing", err);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (startupError) {
    return (
      <div role="alert" style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 16, background: "#f6f7fb" }}>
        <div style={{ maxWidth: 440, background: "#fff", borderRadius: 12, padding: 24, boxShadow: "0 2px 12px rgba(0,0,0,0.08)" }}>
          <h2 style={{ fontSize: 20, margin: "0 0 8px" }}>Business Flows is not available right now</h2>
          <p style={{ margin: "0 0 16px", color: "#444" }}>{startupError}</p>
          <button type="button" className="btn btn-primary" onClick={() => window.location.reload()}>
            Try again
          </button>
        </div>
      </div>
    );
  }
  // An invitation link still opens: accepting it is how access comes back.
  if (noDatabase && authToken && !location.pathname.startsWith("/invite/")) {
    return (
      <NoDatabaseCard
        email={user.email}
        // Which workspace has nothing in it, and the way out of it: the bar
        // that would otherwise switch is not on screen here.
        workspace={workspacesOf(user).length > 1 ? currentWorkspace(user)?.name || "" : ""}
        others={workspacesOf(user).filter((one) => one.id !== currentWorkspace(user)?.id)}
        onChooseWorkspace={(id) => {
          rememberWorkspace(id);
          window.location.reload();
        }}
        onRetry={() => window.location.reload()}
        onSignOut={() => {
          recordSignOut();
          localStorage.clear();
          window.location.assign("/login");
        }}
      />
    );
  }
  if (loading && authToken && authToken !== undefined) return <SplashScreen />;

  // Which features this instance offers — decided by the server (Console →
  // Features), sent with every getProfile and read back through one helper so
  // the routes, Home and the navbar can never disagree.
  const features = readFeatures();

  return (
    <div className="containerMain">
      {/* What users see in the current database; re-read on focus and every 30 s. */}
      <ViewConfigProvider database={(authToken && (localStorage.getItem("db") || user.mainDatabase)) || null}>
      {/*-------------------------------------------------------------------- */
      /*                     if the user is signed-in  */
      /* -------------------------------------------------------------------- */}
      {!isAccountPage && (
      <Navbar
        // The quick search asks the server; the navbar no longer needs the
        // pre-loaded hierarchy trees.
        userEmail={user.email}
        userRole={user.role}
        setNodeDatum={setNodeDatum}
        user={user}
        path={`/${user.mainDatabase}`}
        rootGUID =  {rootGUID}
        databaseOutage={databaseOutage}
      />
      )}
      {/* Switching workspace can take the database with it: one quiet line
          saying so, on the other side of the reload the switch causes. */}
      {!isAccountPage && authToken && <ContextNote />}

      <div className={`main-content-area${isNoScroll || isAccountPage ? " main-content-area--no-scroll main-content-area--flush" : ""}`}>
      {authToken ? (
        // A page that breaks while drawing shows a recovery screen, not a blank
        // app. Keyed on the route, so moving to another page clears it.
        <ErrorBoundary key={location.pathname}>
        <Switch>
          <Route
            exact
            path="/my-account"
            component={() => <MyAccount user={user} />}
          />
          <Route
            exact
            path="/sap-solman"
            component={() => <VisualizeSolMan />}
          />
          <Route
            exact
            path="/admin"
            component={() => (canOpenConsole(user) ? <AdminConsole user={user} /> : <Redirect to="/home" />)}
          />
          <Route
            exact
            path="/admin/:id"
            component={() => (canOpenConsole(user) ? <AdminConsole user={user} /> : <Redirect to="/home" />)}
          />
          {/* One workspace in the console: /admin/workspaces/:workspaceId */}
          <Route
            exact
            path="/admin/:id/:itemId"
            component={() => (canOpenConsole(user) ? <AdminConsole user={user} /> : <Redirect to="/home" />)}
          />
          <Route
            exact
            path={`/home`}
            component={() => (
              <Home
                user={user}
                setUser={setUser}
                path={`/${user.mainDatabase}`}
                rootGUID = {rootGUID}
                databaseOutage={databaseOutage}
              />
            )}
          />
          <Route
            exact
            path={`/home/:orgName`}
            component={() => (
              <Home
                user={user}
                setUser={setUser}
                path={`/${user.mainDatabase}`}
                rootGUID = {rootGUID}
                databaseOutage={databaseOutage}
              />
            )}
          />

          <Route exact path="/:dbName/object/:id">
            {features.relationshipExplorer
              ? <Protected cmp={() => (
                  <CustomD3Tree
                    path={`/${user.mainDatabase}`}
                    nodeDatum={nodeDatum}
                    user={user}
                  />
                )} />
              : <Redirect to="/home" />
            }
          </Route>

          <Route exact path="/:dbName/object">
            {features.relationshipExplorer
              ? <Protected cmp={() => (
                  <CustomD3Tree
                    path={`/${user.mainDatabase}`}
                    nodeDatum={nodeDatum}
                    user={user}
                  />
                )} />
              : <Redirect to="/home" />
            }
          </Route>

          {/* The older link shape, without a database in it: the same explorer,
              opened on the object the link names. */}
          <Route exact path="/relationship-explorer/:id">
            {features.relationshipExplorer
              ? <Protected cmp={() => (
                  <CustomD3Tree
                    path={`/${user.mainDatabase}`}
                    nodeDatum={nodeDatum}
                    user={user}
                  />
                )} />
              : <Redirect to="/home" />
            }
          </Route>

          {/* Links written without a database in them - /object/{guid},
              /map/{guid} and the older /model/{guid} - open in the database
              the reader is in and move to the canonical /{database}/… link,
              query string and all (and /model/ lands on /map/ in one step).
              With no database to open them in, the card says so. */}
          <Route exact path={DATABASE_FREE_PATHS}>
            {canonicalLink(location, user) ? (
              <Redirect to={canonicalLink(location, user)} />
            ) : (
              <NoDatabaseCard
                email={user.email}
                onRetry={() => window.location.reload()}
                onSignOut={() => {
                  recordSignOut();
                  localStorage.clear();
                  window.location.assign("/login");
                }}
              />
            )}
          </Route>

          <Route exact path="/bf-assistant">
            <Protected cmp={() => <Chat user={user} {...props} />} />
          </Route>

          <Route exact path="/flowos">
            {features.flowos
              ? <Protected cmp={() => <FlowOS user={user} />} />
              : <Redirect to="/home" />
            }
          </Route>

          <Route exact path="/workspace">
            <Protected cmp={() => <ManageWorkspace user={user} />} />
          </Route>

          <Route exact path="/developers">
            {SHOW_DEVELOPER_API
              ? <Protected cmp={() => <DevelopersPage user={user} />} />
              : <Redirect to="/home" />
            }
          </Route>

          <Route exact path="/invite/:token">
            <Protected cmp={() => <InvitePage />} />
          </Route>

          <Route exact path="/changepassword">
            <Protected cmp={() => <ChangePassword user={user} />} />
          </Route>

          {/* An object's picture: whatever it has (BPMN, SVG, images, a PDF,
              a Mermaid source) or the map the product draws from the graph.
              /{database}/map/{guid} is the canonical address - the one the
              app writes and a reader copies. */}
          <Route exact path="/:dbName/map/:id">
            {features.businessFlows
              ? <DiagramViewer
                  path={`/${user.mainDatabase}`}
                  user={user}
                  {...props}
                  setNodeDatum={setNodeDatum}
                />
              : <Redirect to="/home" />
            }
          </Route>
          {/* Its address until 28 Sep 2026. "model" named neither the drawing
              nor the list of parts, and every mail and ticket that carries one
              must keep working: it moves to the map address, query string and
              history state and all, and never opens a second viewer. */}
          <Route
            exact
            path="/:dbName/model/:id"
            render={({ match, location: from }) => (
              <Redirect
                to={{
                  pathname: `/${match.params.dbName}/map/${match.params.id}`,
                  search: from.search,
                  state: from.state,
                }}
              />
            )}
          />
          {/* Previous hierarchy-based viewer:
          <Route exact path="/:dbName/model/:id">
            {features.businessFlows
              ? <ModelRenderer
                  // The hierarchy trees are no longer loaded at sign-in;
                  // reviving this viewer means fetching them here.
                  integrationAPIToken={integrationAPIToken}
                  showProperties={true}
                  path={`/${user.mainDatabase}`}
                  width="45vw"
                  height="80vh"
                  propertyDivHeight="80vh"
                  user={user}
                  {...props}
                  setNodeDatum={setNodeDatum}
                />
              : <Redirect to="/home" />
            }
          </Route>
          */}
          <Route exact path="/:dbName/advance-search">
            {features.advanceSearch
              ? <AdvanceSearch
                  {...props}
                  setNodeDatum={setNodeDatum}
                  path={`/${user.mainDatabase}`}
                  user={user}
                />
              : <Redirect to="/home" />
            }
          </Route>

          <Route
            exact
            path="/:dbName/external-link/*"
            component={ExternalLinkMessage}
          ></Route>
          <Route exact path="/release-notes" component={RelaseNotes} />

          {/* -------------------------------------------------------------------- */}
          {/* if the user is signed-in for the given below routes navigate to Home */}
          {/* -------------------------------------------------------------------- */}

          <Route
            exact
            path="/signup"
            component={() => <Home user={user} />}
          />
          <Route
            exact
            path="/signup/*"
            component={() => <Home user={user} />}
          />
          <Route
            exact
            path="/login"
            component={() => <Home user={user} />}
          />
          <Route
            exact
            path="/login/:token"
            component={() => <Home user={user} />}
          />
          <Route
            exact
            path="/success"
            component={() => <Home user={user} />}
          />
          <Route
            exact
            path="/error"
            component={() => <Home user={user} />}
          />
          <Route
            exact
            path="/:dbName/not-found"
            component={() => (
              <NotFoundPage
                {...props}
                path={`/${user.mainDatabase}`}
                history={history}
              />
            )}
          />

          {/* if route does not exists show 404*/}
          <Route
            exact
            path="*"
            component={() => (
              <Home
                user={user}
                setUser={setUser}
                path={`/${user.mainDatabase}`}
              />
            )}
          />
        </Switch>
        </ErrorBoundary>
      ) : (
        /* -------------------------------------------------------------------- */
        /* if the user is NOT signed-in  */
        /* -------------------------------------------------------------------- */
        <Switch>
          {/* Account pages use the full-width split layout */}
          <Route
            exact
            // A deep link opened signed out shows the sign-in page; signing
            // in returns to it (App keeps the path it was asked for).
            path={["/", "/login", "/login/:token", "/changepassword", "/:dbName/map/:id", "/:dbName/model/:id", ...DATABASE_FREE_PATHS]}
            component={Login}
          />
          <Route exact path={["/signup/", "/signup/:regid"]} component={Register} />
          <Route exact path="/forget-password" component={ForgetPassword} />
          <Route exact path="/invite/:token" component={InvitePage} />
          <Route exact path="/setup" component={SetupPage} />
          <Route path="*">
        <div>
          <Container className="auth-page-container">
            <div className="auth-page-inner">
              <Switch>
                <Route exact path="/success" component={ConfirmationMsg} />
                <Route exact path="/error" component={FailureMsg} />
                <Route
                  exact
                  path="/getDocument/:url"
                  component={SPDocService}
                />
                <Route exact path="/release-notes" component={RelaseNotes} />
                <Route
                  exact
                  path="/auth/reset-password/:token"
                  component={ResetPasswordMsg}
                />

                {/* if route does not exists show 404*/}
                <Route
                  exact
                  path="*"
                  component={() => (
                    <NotFoundPage
                      {...props}
                      path={`/${user.mainDatabase}`}
                      history={history}
                    />
                  )}
                />
              </Switch>
            </div>
          </Container>
        </div>
          </Route>
        </Switch>
      )}

      </div>
      {!isAccountPage && <Footer />}
      <SessionExpiredModal show={sessionExpired} onLogin={handleSessionLogin} />
      </ViewConfigProvider>
    </div>
  );
};

export default App;
//test pipeline